/* eslint-disable react/forbid-prop-types */
import React, { useMemo } from 'react';
import { Select, Button, ExpandCollapse, Spacer, Typography } from '@telus-uds/components-web';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { InputWrapper, DateWrapper, ButtonWrapper } from './css';
import { Box, Text } from '../design/css';
import TypeAhead from '../orderDetails/typeAhead';
import 'react-datepicker/dist/react-datepicker.css';
import {
  PROVINCE_MULTISELECT_OPTIONS,
  renderDashboardMultiSelectRows
} from './components';

const Filters = ({
  data,
  updateFilters,
  onApplyFilters,
  onResetFilters,
  filterState,
  staticLabels,
  fieldsToHide = [],
  isWlsFilter = false
}) => {
  const {
    startDate,
    endDate,
    filterCriteria,
    assignee,
    channelId,
    province,
    createdBy,
    status,
    dealership,
    reasonForSubmission,
    orderNotEligibleforMssReason,
    flag,
    reasonForOrderReturned
  } = filterState;
  const {
    completedAt,
    createdAt,
    orderAssignedTo,
    filterByChannel,
    filterByProvince,
    channelName,
    filterByAssignee,
    filterByDates,
    filterBySalesRep,
    filterByStatus,
    startDateLabel,
    endDateLabel,
    buttonApply,
    buttonReset,
    filterByDealership,
    filtersDropdownTitle,
    statusLabel,
    wlsFiltersStatusOptions,
    labelFilterByReasonForSubmission,
    labelFilterByOrderNotEligibleForMSSReason,
    labelFilterByFlags,
    labelFilterByResonReturningOrder
  } = staticLabels;
  const { salesRepNames, channels, assignees, dealerships, reasonForSubmissions = [], orderNotEligibleForMSSReasons = [], flags = [], reasonsForOrderReturned = [] } = data;
  const channelList =
    channels &&
    channels.map(channel => ({ label: channel.text, value: channel.value }));
  const dealershipsList =
    dealerships &&
    dealerships.map(dealership => ({
      label: dealership.name,
      value: dealership.id
    }));
  const reasonForSubmissionList =   reasonForSubmissions && reasonForSubmissions.map(reason => ({ label: reason.text, value: reason.value }));
  const orderNotEligibleforMssReasonList = orderNotEligibleForMSSReasons && orderNotEligibleForMSSReasons.map(reason => ({ label: reason.text, value: reason.value }));
  const flagList = flags && flags.map(flag => ({ label: flag.text, value: flag.value }));
  const reasonsForOrderReturnedList = reasonsForOrderReturned && reasonsForOrderReturned.map(reason => ({ label: reason.text, value: reason.value }));
  const multiSelectFilters = [
    {
      label: filterByProvince,
      name: 'province',
      options: PROVINCE_MULTISELECT_OPTIONS,
      value: province
    },
    {
      label: filterByChannel,
      name: 'channelId',
      options: channelList,
      value: channelId
    },
    {
      label: filterBySalesRep,
      name: 'createdBy',
      options: (salesRepNames || []).map(op => ({
        value: op.id,
        label: `${op.firstName} ${op.lastName}`
      })),
      value: createdBy
    },
    {
      label: filterByStatus,
      name: 'status',
      options: wlsFiltersStatusOptions,
      value: status
    },
    {
      label: filterByDealership,
      name: 'dealership',
      options: dealershipsList,
      value: dealership
    },
    {
      label: labelFilterByReasonForSubmission,
      name: 'reasonForSubmission',
      options: reasonForSubmissionList,
      value: reasonForSubmission,
      multiSelect : false
    },
    {
      label: labelFilterByOrderNotEligibleForMSSReason,
      name: 'orderNotEligibleforMssReason',
      options: orderNotEligibleforMssReasonList,
      value: orderNotEligibleforMssReason
    },
    {
      label: labelFilterByFlags,
      name: 'flag',
      options: flagList,
      value: flag,
      multiSelect: false
    },
    {
      label: labelFilterByResonReturningOrder,
      name: 'reasonForOrderReturned',
      options: reasonsForOrderReturnedList,
      value: reasonForOrderReturned
    }
  ];
  const selectFilters = [
    {
      label: filterByChannel,
      placeholder: channelName,
      options: channels,
      value: channelId,
      onChange: ({ target: { value } }) => updateFilters('channelId', value)
    }
  ];
  const typeAheadFilters = useMemo(
    () =>
      [
        {
          label: filterByAssignee,
          placeholder: orderAssignedTo,
          options: assignees,
          selected: assignee,
          onChange: value => updateFilters('assignee', value)
        }
      ].filter(
        i => (isWlsFilter && i.label !== filterByAssignee) || !isWlsFilter
      ),
    [assignees, channels, salesRepNames, filterState]
  );

  const getSelectFilters = listOfFilters => {
    return listOfFilters.map(
      ({ label, placeholder, options, value, onChange, defaultValue }) => {
        return (
          options && (
            <div className="col-md-4">
              <Select
                label={label}
                placeholder={placeholder}
                initialValue={defaultValue}
                value={value}
                onChange={(selectValue) => {
                  // Convert UDS onChange (receives value directly) to TDS format (event object)
                  if (onChange) {
                    onChange({ target: { value: selectValue } });
                  }
                }}
              >
                {options.map(option => (
                  <Select.Item 
                    key={option.value || option.label || option.text} 
                    value={option.value || option.label || option.text}
                  >
                    {option.text || option.label || option.value}
                  </Select.Item>
                ))}
              </Select>
            </div>
          )
        );
      }
    );
  };
  const getTypeAheadFilters = listOfFilters => {
    return listOfFilters.map(
      ({ label, placeholder, options, selected, onChange }) => {
        return (
          <div className="col-md-4">
            <TypeAhead
              label={label}
              placeholder={placeholder}
              options={options || []}
              selected={selected}
              onChange={onChange}
            />
          </div>
        );
      }
    );
  };

  const getDateFilters = () => {
    return (
      <div className="col-md-4">
        <Box
          style={{ paddingBottom: 6, paddingLeft: 0, marginTop: 10 }}
          className="col-md-12"
        >
          <Text fontWeight="700" fontSize="16px">{filterByDates} </Text>
        </Box>
        {/* {!isWlsFilter && <Box>{getButtonGroup()}</Box>} */}
        <InputWrapper>
          <DateWrapper
            style={{
              marginRight: '15px',
              paddingLeft: 0
            }}
            className="col-md-6"
          >
            <DatePicker
              wrapperClassName="wlsDatePicker"
              dateFormat="yyyy/MM/dd"
              placeholderText={startDateLabel}
              selected={startDate}
              onChange={value => updateFilters('startDate', value)}
            />
          </DateWrapper>
          <DateWrapper
            style={{ marginRight: '5px', paddingLeft: 0 }}
            className="col-md-6"
          >
            <DatePicker
              wrapperClassName="wlsDatePicker"
              dateFormat="yyyy/MM/dd"
              placeholderText={endDateLabel}
              minDate={startDate}
              selected={endDate}
              onChange={value => updateFilters('endDate', value)}
            />
          </DateWrapper>
        </InputWrapper>
      </div>
    );
  };
  return (
    <ExpandCollapse initialOpen={[]}>
      {(expandProps) => (
        <ExpandCollapse.Panel
          {...expandProps}
          panelId="filters"
          control={<Typography variant={{ bold: true }}>{filtersDropdownTitle}</Typography>}
        >
          <div className="row">
            {!isWlsFilter && getSelectFilters(selectFilters)}
            {renderDashboardMultiSelectRows(multiSelectFilters, updateFilters, {
              hiddenFieldNames: fieldsToHide
            })}
            {getTypeAheadFilters(typeAheadFilters)}

            {getDateFilters()}
          </div>
          <Spacer space={3} />
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="row"
          >
            <Button onClick={onApplyFilters} variant={{ priority: 'low' }}>
              {buttonApply}
            </Button>
            <Box width="2"> </Box>
            <Button onClick={onResetFilters}>{buttonReset}</Button>
          </Box>
        </ExpandCollapse.Panel>
      )}
    </ExpandCollapse>
  );
};

export default Filters;

Filters.propTypes = {
  staticLabels: PropTypes.object.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  isWlsFilter: PropTypes.bool,
  filterState: PropTypes.shape({
    filterStatus: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    filterCriteria: PropTypes.string,
    assignee: PropTypes.array,
    status: PropTypes.array,
    dealership: PropTypes.array,
    channelId: PropTypes.string,
    province: PropTypes.string,
    channelOrgCode: PropTypes.string,
    outletCode: PropTypes.string,
    createdBy: PropTypes.string
  }).isRequired,
  fieldsToHide: PropTypes.arrayOf(PropTypes.array),
  data: PropTypes.arrayOf(PropTypes.object).isRequired
};
